import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { appStore } from '@tom-ui/utils'
import { AllocationRuleStore } from '@yard/stores/allocation-rule.store'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useYardSetupMessageHandler } from '../../hooks/useYardSetupMessageHandler'
import { IYardBlockFormData } from '../../models/yard-setup.model'
import { YardBlockViewStore } from '../../stores/nodes/yard-block.view-store'
import { YardBlockAdjustMaxTierDialog } from '../molecules/YardBlockAdjustMaxTierDialog'
import { YardBlockForm } from '../molecules/YardBlockForm'
import { YardBlockRenameSectionDialog } from '../molecules/YardBlockRenameSectionDialog'
import { YardBlockSpaceBetweenBaysDialog } from '../molecules/YardBlockSpaceBetweenBaysDialog'

interface Props {
  yardBlockViewStore: YardBlockViewStore
  allocationRuleStore: AllocationRuleStore
}

export const YardBlockDialogs = observer(({ yardBlockViewStore, allocationRuleStore }: Props) => {
  const { t } = useTranslate()
  const { getExceptionMessage, getBlockSuccessMessage, getBlockDeleteSuccessMessage } =
    useYardSetupMessageHandler()

  const onSubmit = async (data: IYardBlockFormData) => {
    try {
      await yardBlockViewStore.submit(data)
      appStore.setShowAlert('success', getBlockSuccessMessage(!!yardBlockViewStore.editingId))
      yardBlockViewStore.closeFormDialog()
    } catch (e: any) {
      appStore.setShowAlert('error', getExceptionMessage(e))
    }
  }

  const hasAllocationRules = useMemo(() => {
    if (!yardBlockViewStore.editingId) return false
    return allocationRuleStore.items.some(rule =>
      rule.destinations.some(dest => dest.blockId === yardBlockViewStore.editingId),
    )
  }, [allocationRuleStore.items, yardBlockViewStore.editingId])

  const onDelete = async () => {
    try {
      await yardBlockViewStore.delete(yardBlockViewStore.editingId!)
      appStore.setShowAlert('success', getBlockDeleteSuccessMessage())
      yardBlockViewStore.setDeleteDialogOpen()
    } catch (e: any) {
      appStore.setShowAlert('error', getExceptionMessage(e))
    }
  }

  return (
    <>
      <ConfirmationDialog
        open={yardBlockViewStore.isFormDialogOpen}
        title={
          yardBlockViewStore.focusedYardBlock
            ? t('editYardBlock', 'Edit yard block')
            : t('addYardBlock', 'Add yard block')
        }
        primaryActionText={
          yardBlockViewStore.focusedYardBlock
            ? t('yesUpdate', 'Yes, update')
            : t('yesCreate', 'Yes, create')
        }
        closeLabel={t('noCancel', 'No, cancel')}
        formId='yard-block-form'
        maxWidth='md'
        onClose={() => yardBlockViewStore.closeFormDialog()}
      >
        <YardBlockForm
          id='yard-block-form'
          yardBlock={yardBlockViewStore.focusedYardBlock}
          hasOccupancies={yardBlockViewStore.hasFocusedBlockOccupancies}
          onSubmit={onSubmit}
        />
      </ConfirmationDialog>

      <ConfirmationDialog
        open={yardBlockViewStore.isDeleteDialogOpen}
        title={t('deleteYardBlock', 'Delete yard block')}
        message={t(
          'deleteYardBlockAllocationRuleWarning',
          'This yard block has allocation rules assigned to it. Deleting it will remove those assignments.',
        )}
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('noCancel', 'No, cancel')}
        onClose={() => yardBlockViewStore.setDeleteDialogOpen()}
        onConfirm={onDelete}
        maxWidth='xs'
      />

      <YardBlockRenameSectionDialog yardBlockViewStore={yardBlockViewStore} />
      <YardBlockAdjustMaxTierDialog yardBlockViewStore={yardBlockViewStore} />
      <YardBlockSpaceBetweenBaysDialog yardBlockViewStore={yardBlockViewStore} />
    </>
  )
})
